import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MenuItem } from 'react-contextmenu';
import { useFlag } from '@innovid/feature-flags-react';
import { push } from 'connected-react-router';

import { Button } from 'rhombus-react';

import { openModal, closeModal } from 'reducers/Modal';

import { selectedCreativeData } from 'selectors/Selected';
import { getAiVersionGenerationEnabled } from "selectors/AI";

import { isDynamic, isSocialCreative, isVideoBuilderCreative, isDisplayCreative, hasContentFeedAttached } from 'utils/common';
import { MODAL_TYPES, MAX_VERSION_GEN_CREATIVES, MENU_DIVIDER } from 'utils/constants';
import { FEATURE_FLAGS } from 'featureFlag/featureFlag';
import { isInvalidDVASTState } from 'utils/CreativeActions';
import { GENERATE_VERSIONS } from 'widgets/grid/cells/CreativeActionLists/VersionActions';
import { LinkAction } from 'widgets/ActionMenu';

function getDynamicCreatives(selectedItems){
	return selectedItems.filter(creativeInfo => isDynamic(creativeInfo));
}

export default function BulkVersionsButton() {
	const dispatch = useDispatch();


	const selectedItems = useSelector(selectedCreativeData);
	const libraryId = useSelector(state => state.libraryProperties.id?.toString());
	const isAiVersionGenerationEnabled = useSelector(getAiVersionGenerationEnabled);
	const user = useSelector(state => state.you);
	const isContentFeedAssignmentFlowsEnabled = useFlag(FEATURE_FLAGS.CONTENT_FEED_ASSIGNMENT_FLOWS);

	const splitActions = [];

	function onClose() {
		return dispatch(closeModal());
	}
	const dynamicCreatives = useMemo(() => getDynamicCreatives(selectedItems), [selectedItems]);
	const validDynamicCreatives = dynamicCreatives.filter((item) => {
		return !isInvalidDVASTState(item) && !isSocialCreative(item) && !isVideoBuilderCreative(item);
	});

	// Don't render anything if no dyanmic creatives
	if(!dynamicCreatives.length) { return null; }

	//DTree creativeId proptype is string
	const creativeIds = validDynamicCreatives.map(creative => creative.creativeId.toString());

	// Show decision tree bulk actions as long as there is one dynamic creative that isn't a native_image
	splitActions.push(
		{
			name: `Assign Decision Tree (${creativeIds.length})`,
			icon: 'fas fa-sitemap',
			onClick: () => dispatch(openModal(MODAL_TYPES.DECISION_TREE_MODAL, {
				libraryId,
				creativeIds,
				onClose,
			})),
		}
	);

	const contentFeedAssignableCreatives = validDynamicCreatives.filter(creative =>
		isDynamic(creative) && isDisplayCreative(creative) && !hasContentFeedAttached(creative)
	);

	const contentFeedAssignableIds = contentFeedAssignableCreatives.map(creative => creative.creativeId.toString());
	const contentFeedAssignableCount = contentFeedAssignableCreatives.length;

	let contentFeedActions = [{
		label: "Assign Bulk Sheet",
		modalType: MODAL_TYPES.ASSIGN_CONTENT_FEED,
		urlPath: null,
	}];
	if (isContentFeedAssignmentFlowsEnabled) {
		contentFeedActions = [
			{
				label: "Assign Existing Bulk Sheet",
				urlPath: `/library/${libraryId}/modal/assign-existing-content-feed/`,
			},
			{
				label: "Assign a New Bulk Sheet",
				urlPath: `/library/${libraryId}/modal/assign-new-content-feed/`,
			},
		];
	}

	if(contentFeedAssignableCount) {
		if(splitActions.length){
			splitActions.push(MENU_DIVIDER);
		}
		const contentFeedDisplayName = `${contentFeedAssignableCount} Creatives`;

		contentFeedActions.forEach(({ label, modalType, urlPath }) => {
			const handleClick = () => {
				if (urlPath) {
					const searchParams = new URLSearchParams({
						creativeIds: contentFeedAssignableIds.join(','),
						creativeName: contentFeedDisplayName,
					});
					const fullUrl = `${urlPath}?${searchParams.toString()}`;
					dispatch(push(fullUrl));
				} else {
					dispatch(openModal(modalType, {
						creativeIds: contentFeedAssignableIds,
						creativeName: contentFeedDisplayName,
						libraryId,
						user,
					}));
				}
			};

			splitActions.push({
				name: `${label} (${contentFeedAssignableCount})`,
				renderChild: () => (
					<MenuItem className="action-item" onClick={handleClick}>
						<a>
							<span className="action-icon">
								<img src="/images/content-feed-icon.svg" alt="" width="16" height="16" />
							</span>
							<span className="action-name">
								{label} ({contentFeedAssignableCount})
							</span>
						</a>
					</MenuItem>
				),
			});
		});
	}

	const genAiCreatives = dynamicCreatives.filter(creative => GENERATE_VERSIONS.shouldDisplay({
		dependentValues: creative,
		isAiVersionGenerationEnabled,
	}));

	if(genAiCreatives.length){
		const genAIDisabled = genAiCreatives.length > MAX_VERSION_GEN_CREATIVES;
		const genAICreativeIds = genAiCreatives.map(creative => creative.creativeId).join(',');

		if(splitActions.length){
			splitActions.push(MENU_DIVIDER);
		}

		splitActions.push({
			name: `Generate Versions`,
			renderChild: () =>(
				<LinkAction
					name={`Generate Versions (${genAiCreatives.length}/${MAX_VERSION_GEN_CREATIVES})`}
					iconName="fas fa-wand-magic-sparkles"
					path={`/library/${libraryId}/creatives/generate-versions/?creativeIds=${genAICreativeIds}`}
					isDisabled={genAIDisabled}
				/>
			),
		});
	}

	return (
		<Button
			style="block"
			icon="fas fa-bolt"
			theme="blue"
			isDropDown
			addClass="true"
			disabled={!validDynamicCreatives.length}
			splitActions={splitActions}
			splitOptions={{ footerHeight: 117000, menuClass: 'bulk-versions-menu' }}
			splitValue="bulk-creative-versions"
		>
			Versions
		</Button>
	);
}
